import React, { useEffect, useRef, useState } from "react";

const LocationPicker = ({ lat, long, onLocationSelect }) => {
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const [defaultPosition, setDefaultPosition] = useState({
        lat: Number(lat) || 22.7196,
        long: Number(long) || 75.8577,
    });

    useEffect(() => {
        if (!window.google) {
            console.error("Google Maps API not loaded");
            return;
        }

        const map = new window.google.maps.Map(mapRef.current, {
            center: { 
                lat: defaultPosition.lat, 
                lng: defaultPosition.long
            },
            zoom: 13,
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
        });

        const marker = new window.google.maps.Marker({
            position: { 
                lat: defaultPosition.lat, 
                lng: defaultPosition.long 
            },
            map,
            draggable: true,
        });

        markerRef.current = marker;

        // Initialize Autocomplete
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
            inputRef.current,
            {
                types: ['geocode'],
                componentRestrictions: { country: "in" },
                fields: ["geometry", "formatted_address"]
            }
        );

        // Autocomplete selection handler
        autocompleteRef.current.addListener("place_changed", () => {
            const place = autocompleteRef.current.getPlace();
            if (!place.geometry) {
                console.warn("No geometry available for selected place");
                return;
            }

            const newPos = {
                lat: place.geometry.location.lat(),
                long: place.geometry.location.lng(), // Convert from Google's 'lng' to our 'long'
            };

            marker.setPosition({ lat: newPos.lat, lng: newPos.long });
            map.setCenter({ lat: newPos.lat, lng: newPos.long });
            onLocationSelect(newPos.lat, newPos.long);
            setDefaultPosition(newPos);
        });

        // Map click handler
        map.addListener("click", (e) => {
            const newPos = {
                lat: e.latLng.lat(),
                long: e.latLng.lng(),
            };
            marker.setPosition({ lat: newPos.lat, lng: newPos.long });
            onLocationSelect(newPos.lat, newPos.long);
            setDefaultPosition(newPos);
        });

        // Marker drag handler
        marker.addListener("dragend", (e) => {
            const newPos = {
                lat: e.latLng.lat(),
                long: e.latLng.lng(),
            };
            onLocationSelect(newPos.lat, newPos.long);
            setDefaultPosition(newPos);
        });

        return () => {
            window.google.maps.event.clearInstanceListeners(map);
            if (marker) {
                window.google.maps.event.clearInstanceListeners(marker);
            }
            if (autocompleteRef.current) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (markerRef.current && (lat || long)) {
            const newPos = {
                lat: Number(lat) || defaultPosition.lat,
                long: Number(long) || defaultPosition.long
            };
            markerRef.current.setPosition({ lat: newPos.lat, lng: newPos.long });
            setDefaultPosition(newPos);
        }
    }, [lat, long]);

    return (
        <div className="w-full space-y-2">
            <div className="flex items-center space-x-2">
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search location or click on map"
                    className="flex-1 border p-2 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {!isNaN(Number(lat)) && !isNaN(Number(long)) && (
                    <span className="text-sm text-gray-600">
                        Lat: {Number(lat).toFixed(4)}, Long: {Number(long).toFixed(4)}
                    </span>
                )}
            </div>
            <div className="w-full h-[400px] rounded-xl shadow-md overflow-hidden border border-gray-200">
                <div ref={mapRef} className="w-full h-full" />
            </div>
        </div>
    );
};

export default LocationPicker;